import { t, Instance, flow } from 'mobx-state-tree';
import { StoryModel, createStoryModelFromResponse } from './models/Story';
import { storiesApi } from '@/api/storiesWrapper';
import { generateUUID } from '@/utils/uuid';
import type { CreateStoryPayload, UpdateStoryPayload } from '@/types/story';
import type { MemoriesFilters } from '@/types/memories';
import { DEFAULT_MEMORIES_FILTERS } from '@/types/memories';
import type { StoryResponse } from 'we-tree-api-client/v2';

async function syncStoryMedia(
  familyId: string,
  storyId: string,
  existing: { id: string; mediaAssetId: string }[],
  desiredAssetIds: string[]
): Promise<void> {
  const desiredSet = new Set(desiredAssetIds);
  const existingByAsset = new Map(existing.map((item) => [item.mediaAssetId, item]));

  for (const attachment of existing) {
    if (!desiredSet.has(attachment.mediaAssetId)) {
      await storiesApi.unlinkStoryMedia(familyId, storyId, attachment.id);
    }
  }

  for (const assetId of desiredAssetIds) {
    if (!existingByAsset.has(assetId)) {
      await storiesApi.linkStoryMedia(familyId, storyId, {
        id: generateUUID(),
        mediaAssetId: assetId,
        refRole: 'attachment'
      });
    }
  }
}

const StoryAuthorModel = t.model('StoryAuthorModel', {
  userId: t.identifier,
  displayName: t.string
});

export const FamilyStoriesStore = t
  .model('FamilyStoriesStore', {
    familyId: t.maybeNull(t.string),
    items: t.array(StoryModel),
    authors: t.array(StoryAuthorModel),
    total: t.optional(t.number, 0),
    page: t.optional(t.number, 1),
    pageSize: t.optional(t.number, 20),
    filters: t.frozen<MemoriesFilters>(DEFAULT_MEMORIES_FILTERS),
    isLoading: t.optional(t.boolean, false),
    isLoadingAuthors: t.optional(t.boolean, false),
    error: t.maybeNull(t.string),
    detailStoryId: t.maybeNull(t.string),
    relatedStories: t.array(StoryModel)
  })
  .views((self) => ({
    getStoryById(storyId: string): Instance<typeof StoryModel> | undefined {
      return self.items.find((item) => item.id === storyId) ?? self.relatedStories.find((item) => item.id === storyId);
    },

    get loadedDetailStory(): Instance<typeof StoryModel> | undefined {
      if (!self.detailStoryId) return undefined;
      return (
        self.items.find((item) => item.id === self.detailStoryId) ??
        self.relatedStories.find((item) => item.id === self.detailStoryId)
      );
    },

    get uniqueMemberCount(): number {
      const ids = new Set<string>();
      self.items.forEach((item) => {
        if (item.familyMemberId) ids.add(item.familyMemberId);
      });
      return ids.size;
    },

    get authorFilterOptions(): { id: string; label: string }[] {
      return self.authors
        .map((author) => ({ id: author.userId, label: author.displayName }))
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }))
  .actions((self) => {
    const loadStoryAuthors = flow(function* (familyId: string) {
      if (!familyId) return;
      self.isLoadingAuthors = true;
      try {
        const result = yield storiesApi.listStoryAuthors(familyId);
        if (result.success && result.data) {
          self.authors.replace(
            (result.data.items || []).map((author: { userId: string; displayName: string }) =>
              StoryAuthorModel.create({
                userId: author.userId,
                displayName: author.displayName || 'Unknown author'
              })
            )
          );
          if (
            self.filters.authorUserId &&
            !result.data.items.some((author: { userId: string }) => author.userId === self.filters.authorUserId)
          ) {
            self.filters = { ...self.filters, authorUserId: null };
          }
        } else {
          self.authors.clear();
        }
      } catch {
        self.authors.clear();
      } finally {
        self.isLoadingAuthors = false;
      }
    });

    return {
      setFilters(filters: MemoriesFilters) {
        self.filters = filters;
      },

      clear() {
        self.familyId = null;
        self.items.clear();
        self.authors.clear();
        self.total = 0;
        self.page = 1;
        self.filters = DEFAULT_MEMORIES_FILTERS;
        self.isLoading = false;
        self.isLoadingAuthors = false;
        self.error = null;
        self.detailStoryId = null;
        self.relatedStories.clear();
      },

      setError(message: string | null) {
        self.error = message;
      },

      loadStoryAuthors,

      searchStories: flow(function* (familyId: string, filtersOverride?: Partial<MemoriesFilters>) {
        const filters = { ...self.filters, ...filtersOverride };
        self.filters = filters;
        self.familyId = familyId;
        self.isLoading = true;
        self.error = null;

        try {
          const result = yield storiesApi.searchStories(familyId, {
            query: filters.query.trim() || undefined,
            familyMemberId: filters.familyMemberId ?? undefined,
            authorUserId: filters.authorUserId ?? undefined,
            hasAttachments: filters.hasAttachments ?? undefined,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            page: self.page,
            pageSize: self.pageSize
          });

          if (result.success && result.data) {
            self.items.clear();
            const seenIds = new Set<string>();
            (result.data.items || []).forEach((item: StoryResponse) => {
              if (!item?.id || seenIds.has(item.id)) return;
              seenIds.add(item.id);
              try {
                self.items.push(createStoryModelFromResponse(item, false));
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('[FamilyStoriesStore] Failed to create story model:', error);
              }
            });
            // Prefer API total; fall back to loaded unique items so the results bar matches the feed.
            self.total = result.data.total ?? self.items.length;
            self.page = result.data.page ?? 1;
            self.pageSize = result.data.pageSize ?? self.pageSize;
          } else {
            self.error = result.error?.getUserMessage() || 'Failed to load memories';
            self.items.clear();
            self.total = 0;
          }
        } catch (error) {
          self.error = error instanceof Error ? error.message : 'An unexpected error occurred';
          self.items.clear();
          self.total = 0;
        } finally {
          self.isLoading = false;
        }
      }),

      loadStoryDetail: flow(function* (familyId: string, storyId: string) {
        self.familyId = familyId;
        self.detailStoryId = storyId;
        self.error = null;

        try {
          const result = yield storiesApi.getStory(familyId, storyId);
          if (result.success && result.data) {
            const storyModel = createStoryModelFromResponse(result.data, true);
            const existingIndex = self.items.findIndex((item) => item.id === storyId);
            if (existingIndex >= 0) {
              self.items.splice(existingIndex, 1, storyModel);
            } else {
              // Keep detail in store so updateStory can sync media attachments
              self.items.push(storyModel);
            }
            return { success: true, data: result.data, story: storyModel };
          }
          self.error = result.error?.getUserMessage() || 'Failed to load memory';
          return { success: false, error: result.error };
        } catch (error) {
          self.error = error instanceof Error ? error.message : 'Failed to load memory';
          return { success: false, error: error instanceof Error ? error : new Error('Failed to load memory') };
        }
      }),

      loadRelatedStories: flow(function* (familyId: string, familyMemberId: string, excludeStoryId: string) {
        try {
          const result = yield storiesApi.searchStories(familyId, {
            familyMemberId,
            excludeId: excludeStoryId,
            page: 1,
            pageSize: 3,
            sortBy: 'createdAt',
            sortOrder: 'desc'
          });

          self.relatedStories.clear();
          if (result.success && result.data) {
            (result.data.items || []).forEach((item: StoryResponse) => {
              try {
                self.relatedStories.push(createStoryModelFromResponse(item, false));
              } catch {
                // ignore invalid rows
              }
            });
          }
        } catch {
          self.relatedStories.clear();
        }
      }),

      createStory: flow(function* (familyId: string, payload: CreateStoryPayload) {
        const storyId = generateUUID();
        try {
          const result = yield storiesApi.createStory(familyId, {
            id: storyId,
            familyMemberId: payload.familyMemberId,
            title: payload.title.trim(),
            content: payload.content.trim()
          });

          if (!result.success || !result.data) {
            return { success: false, error: result.error };
          }

          const assetIds = payload.mediaAssetIds ?? [];
          if (assetIds.length > 0) {
            yield syncStoryMedia(familyId, storyId, [], assetIds);
          }

          const detailResult = yield storiesApi.getStory(familyId, storyId);
          const response = detailResult.success && detailResult.data ? detailResult.data : result.data;
          const storyModel = createStoryModelFromResponse(response, true);
          self.items.unshift(storyModel);
          self.total += 1;
          yield loadStoryAuthors(familyId);
          return { success: true, data: response, storyId };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error : new Error('Failed to create memory')
          };
        }
      }),

      updateStory: flow(function* (familyId: string, storyId: string, payload: UpdateStoryPayload) {
        try {
          const existing = self.getStoryById(storyId);
          const result = yield storiesApi.updateStory(familyId, storyId, {
            title: payload.title.trim(),
            content: payload.content.trim()
          });

          if (!result.success || !result.data) {
            return { success: false, error: result.error };
          }

          if (payload.mediaAssetIds) {
            let currentAttachments =
              existing?.mediaAttachments.map((a) => ({ id: a.id, mediaAssetId: a.mediaAssetId })) ?? [];
            if (!existing) {
              const detailResult = yield storiesApi.getStory(familyId, storyId);
              if (detailResult.success && detailResult.data?.mediaAttachments) {
                currentAttachments = detailResult.data.mediaAttachments.map(
                  (a: { id: string; mediaAssetId: string }) => ({
                    id: a.id,
                    mediaAssetId: a.mediaAssetId
                  })
                );
              }
            }
            yield syncStoryMedia(familyId, storyId, currentAttachments, payload.mediaAssetIds);
          }

          const detailResult = yield storiesApi.getStory(familyId, storyId);
          const response = detailResult.success && detailResult.data ? detailResult.data : result.data;
          const storyModel = createStoryModelFromResponse(response, true);
          const index = self.items.findIndex((item) => item.id === storyId);
          if (index >= 0) {
            self.items.splice(index, 1, storyModel);
          } else {
            self.items.push(storyModel);
          }
          return { success: true, data: response };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error : new Error('Failed to update memory')
          };
        }
      }),

      deleteStory: flow(function* (familyId: string, storyId: string) {
        try {
          const result = yield storiesApi.deleteStory(familyId, storyId);
          if (result.success) {
            const index = self.items.findIndex((item) => item.id === storyId);
            if (index >= 0) {
              self.items.splice(index, 1);
              self.total = Math.max(0, self.total - 1);
            }
            if (self.detailStoryId === storyId) {
              self.detailStoryId = null;
            }
            yield loadStoryAuthors(familyId);
            return { success: true };
          }
          return { success: false, error: result.error };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error : new Error('Failed to delete memory')
          };
        }
      })
    };
  });

export type FamilyStoriesStoreType = Instance<typeof FamilyStoriesStore>;

export const familyStoriesStore = FamilyStoriesStore.create({});
